/**
 * Data layer for `customer-io-integration`. Thin typed fetch wrappers over /api/v1/customer-io-integration.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface CustomerIoIntegrationRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListCustomerIoIntegrationResponse {
	count: number;
	items: CustomerIoIntegrationRow[];
}

export interface CreateCustomerIoIntegrationBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/customer-io-integration";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListCustomerIoIntegrationResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListCustomerIoIntegrationResponse>(res);
}

export async function create(body: CreateCustomerIoIntegrationBody): Promise<CustomerIoIntegrationRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<CustomerIoIntegrationRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const customerIoIntegrationQueryKeys = {
	all: ["customer-io-integration"] as const,
	list: () => [...customerIoIntegrationQueryKeys.all] as const,
	detail: (id: string) => [...customerIoIntegrationQueryKeys.all, id] as const,
};
